import type {FunnelChartItem} from './funnel-chart';

export type FunnelChartRenderItem = FunnelChartItem & {
    width: number;
    delta: string | null | undefined;
};

// Max width for widest funnel section (top)
const MAX_WIDTH_PERCENTAGE = 100;
// Min width for narrowest funnel section (bottom)
const MIN_WIDTH_PERCENTAGE = 20;
// Range of width values to distribute across funnel sections
const WIDTH_RANGE = MAX_WIDTH_PERCENTAGE - MIN_WIDTH_PERCENTAGE;

/*
 * Adds calculated properties that inform the render of FunnelChartItems:
 * - 'width': Creates a tapering visual effect for the funnel (100% to 20%)
 * - 'delta': Percentage advanced to next stage (conversion rate)
 */
export const addFunnelRenderProperties = (items: FunnelChartItem[]): FunnelChartRenderItem[] => {
    // Handle empty array
    if (items.length === 0) return [];

    // Single item: full width, no delta
    if (items.length === 1) {
        return items.map((item) => ({...item, width: 100, delta: null}));
    }

    // Special case: for two items, use 100% and 50% for better visual contrast
    if (items.length === 2) {
        return items.map((item, i) => {
            const width = i === 0 ? 100 : 50;
            // @ts-expect-error
            const delta = getDelta(item.value, items[1].value);

            return {...item, width, delta};
        });
    }

    // How much to decrease width for each subsequent item
    const widthDecrement = Math.round(WIDTH_RANGE / (items.length - 1));

    // Track the current width as we iterate through items
    // Start at 100% + widthDecrement because we subtract before using it
    let currentWidth = 100 + widthDecrement;

    return items.map((item, i) => {
        // Calculate width
        currentWidth -= widthDecrement;

        // Calculate delta (percentage advanced to next stage)
        // @ts-expect-error
        const nextValue = i === items.length - 1 ? null : items[i + 1].value;
        const delta = getDelta(item.value, nextValue);

        return {...item, width: currentWidth, delta};
    });
};

export const getDelta = (
    currentValue: number,
    nextValue?: number | null
): string | null | undefined => {
    // Last item or current value is 0 - no delta to calculate
    if (nextValue == null || currentValue === 0) {
        return null;
    }

    // Next value is 0 - delta is a "true" zero
    if (nextValue === 0) {
        return '0%';
    }

    const value = Math.round((nextValue / currentValue) * 100);

    return value === 0 ? '< 1%' : `${value}%`;
};
